import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightArrowLeft,
  faBan,
  faCheck,
  faCircleInfo,
  faCopy,
  faDownload,
  faFileLines,
  faXmarkCircle,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { Modal } from "react-bootstrap";
import { StatusBadge } from "../Table/cells";
import TransactionDetailsSkeleton from "../Skeleton/TransactionDetailsSkeleton";
import UtcToLocal from "../UtcToLocal";
import "../../Styles/transaction-details-panel.scss";
import HTMLtoPDFGenerator from "../ReceiptDownload/HTMLtoPDFGenerator";
import QrDownload from "../../../components/QrDownload";
import useNewTransactionDetailsAPI from "../../api/transactionDetailsAPI";
import { useDispatch, useSelector } from "react-redux";
import { setActionPayload, setActionType } from "../../../features/authorizationSlice";
import { ACTION_TYPES } from "../../../app/actionTypes";
import { resetRequest } from "../../../features/transactionDetailsSlice";
import usePayrollService from "../../api/usePayrollService";
import useNewButlerServiceAPI from "../../api/butlerServiceAPI";
import useNewCashDeliveryAPI from "../../api/cashDeliveryAPI";
import useNewQuickPayAPI from "../../api/quickPayAPI";
import useCopyFeedback from "../../hooks/useCopyFeedback";
import {
  buildTransactionDetailsView,
  dateOptions,
  formatTransactionAmount,
  formatTransferAbroadAmount,
} from "./detailsMapping";

const MOBILE_BREAKPOINT = 991;

function RenderFieldValue({ item }) {
  if (item.value?.type === "date") {
    return (
      <UtcToLocal utcTimestamp={item.value.value} options={dateOptions} />
    );
  }

  if (item.value?.type === "link") {
    return (
      <a href={item.value.href} target="_blank" rel="noreferrer">
        {item.value.text}
      </a>
    );
  }

  if (typeof item.value === "boolean") {
    return item.value ? "Yes" : "No";
  }

  return item.value;
}

function SummaryIcon({ iconUrl }) {
  if (iconUrl) {
    return <img src={iconUrl} alt="" />;
  }
  return <FontAwesomeIcon icon={faArrowRightArrowLeft} />;
}

function CancelActionModal({
  cancelRequest,
  onClose,
  onConfirm,
}) {
  const dialogClassName =
    cancelRequest?.type === "butler"
      ? "new-butler-cancel-modal"
      : "new-cash-cancel-modal";

  return (
    <Modal
      centered
      show={Boolean(cancelRequest)}
      onHide={onClose}
      dialogClassName={dialogClassName}
    >
      <Modal.Body className={`${dialogClassName}__body`}>
        <div className={`${dialogClassName}__header`}>
          <div>
            <h3>{cancelRequest?.title || "Cancel Request"}</h3>
            <p>{cancelRequest?.description || "Are you sure you want to cancel this request?"}</p>
          </div>
          <button type="button" className="btn-close" onClick={onClose} />
        </div>

        <div className={`${dialogClassName}__actions`}>
          <button type="button" className={`${dialogClassName}__secondary`} onClick={onClose}>
            Not now
          </button>
          <button type="button" className={`${dialogClassName}__danger`} onClick={onConfirm}>
            {cancelRequest?.confirmLabel || "Confirm"}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

const transferAbroadHiddenKeys = [
  "BSLFormSections",
  "Details",
  "Documents",
  "ExternalTransferTypeItem",
  "GlobalCode",
  "Icon",
  "Receipt",
  "ShowDownloadButton",
  "ShowDownloadReceipt",
  "ShowPayrollDetailsButton",
  "ShowRepeatButton",
  "ShowRepeatTransfer",
  "ShowRepeatTransferButton",
];

export default function TransactionDetailsPanel({
  details,
  error,
  loading,
  onClose,
  extraActions = [],
  hiddenKeys = [],
  variant = "default",
  transactionId = null,
  additionalInfo = "",
  status: transactionStatus = "",
  onOpenQrAfterGenerate,
  feature = "",
  method = "",
  account = "",
}) {
  const [qrCode, setQrCode] = useState("");
  const [cancelRequest, setCancelRequest] = useState(null);
  const [overviewActionError, setOverviewActionError] = useState("");
  const [isMobileViewport, setIsMobileViewport] = useState(
    () =>
      typeof window !== "undefined"
        ? window.innerWidth <= MOBILE_BREAKPOINT
        : false,
  );
  const dispatch = useDispatch();
  const quickPayCheckerAdvanceId = useSelector(
    (state) => state.quickPay?.checkerAdvanceId,
  );
  const { GetTransactionDetails, detailsLoading, detailsError } =
    useNewTransactionDetailsAPI();
  const { GetPayrollSummaryByReferenceNo } = usePayrollService();
  const { CancelButlerDelivery } = useNewButlerServiceAPI();
  const { CancelCashOrder } = useNewCashDeliveryAPI();
  const { GetQuickPaySummary } = useNewQuickPayAPI();
  const { isCopied, copyWithFeedback } = useCopyFeedback();
  const cacheRef = useRef(new Map());
  const queryKey = useMemo(
    () =>
      `${transactionId || ""}::${additionalInfo || ""}::${transactionStatus || ""}`,
    [transactionId, additionalInfo, transactionStatus],
  );

  useEffect(() => {
    let mounted = true;
    if (!transactionId || details) return undefined;

    if (cacheRef.current.has(queryKey)) {
      return undefined;
    }

    GetTransactionDetails(transactionId, additionalInfo, transactionStatus)
      .then((payload) => {
        if (!mounted) return;
        cacheRef.current.set(queryKey, payload);
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, [additionalInfo, details, queryKey, transactionId, transactionStatus]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => {
      setIsMobileViewport(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setOverviewActionError("");
  }, [transactionId, details, additionalInfo, transactionStatus]);

  const resolvedDetails =
    details || cacheRef.current.get(queryKey) || null;
  const resolvedLoading = Boolean(!details && transactionId && detailsLoading) || loading;
  const resolvedError = error || (!details && transactionId ? detailsError : null);
  const isViewer = sessionStorage.getItem("EntityRole") === "VIEWER";

  const handleApproveReject = useCallback(
    (status) => {
      const actionTargetId =
        resolvedDetails?.TransactionID || resolvedDetails?.OrderID || transactionId;
      if (!actionTargetId) return;

      dispatch(resetRequest());

      // Old behavior: payroll batch approval uses APPROVE/REJECT_PAYROLL,
      // while regular transaction approvals use APPROVE_TRX.
      if (resolvedDetails?.ShowPayrollDetailsButton) {
        dispatch(
          setActionType(
            status === "Approve"
              ? ACTION_TYPES.APPROVE_PAYROLL
              : ACTION_TYPES.REJECT_PAYROLL,
          ),
        );
        dispatch(
          setActionPayload({
            Reference: actionTargetId,
            PayrollIds: [],
          }),
        );
      } else {
        dispatch(setActionType(ACTION_TYPES.APPROVE_TRX));
        dispatch(
          setActionPayload({
            TransactionID: actionTargetId,
            Status: status,
            IsQuickPayAmendment: method === "Quick Pay",
            IsPendingBankTransfer: account === "BSL",
          }),
        );
      }

      onOpenQrAfterGenerate?.();
    },
    [
      account,
      method,
      resolvedDetails?.ShowPayrollDetailsButton,
      resolvedDetails,
      transactionId,
    ],
  );

  const handleViewOverview = useCallback(() => {
    setOverviewActionError("");
    const isQuickPay = String(resolvedDetails?.TransactionType || "")
      .toLowerCase()
      .includes("quick pay");

    if (isQuickPay) {
      if (!quickPayCheckerAdvanceId) {
        setOverviewActionError("Unable to open Quick Pay details right now.");
        return;
      }

      GetQuickPaySummary(quickPayCheckerAdvanceId).catch((error) => {
        setOverviewActionError(
          error?.response?.data?.PopupDescription ||
            error?.response?.data?.Message ||
            error?.response?.data?.PopupTitle ||
            "Unable to open Quick Pay details right now.",
        );
      });
      return;
    }

    const reference =
      resolvedDetails?.TransactionID || resolvedDetails?.OrderID || transactionId;
    if (!reference) return;
    GetPayrollSummaryByReferenceNo(reference).catch((error) => {
      setOverviewActionError(
        error?.response?.data?.PopupDescription ||
          error?.response?.data?.Message ||
          "Unable to open details right now.",
      );
    });
  }, [
    GetPayrollSummaryByReferenceNo,
    GetQuickPaySummary,
    quickPayCheckerAdvanceId,
    resolvedDetails,
    transactionId,
  ]);

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelRequest?.transactionId) return;

    if (cancelRequest.type === "butler") {
      await CancelButlerDelivery(cancelRequest.transactionId);
    } else if (cancelRequest.type === "cash") {
      await CancelCashOrder(cancelRequest.flowType, cancelRequest.transactionId);
    }

    setCancelRequest(null);
    onClose?.();
  }, [CancelButlerDelivery, CancelCashOrder, cancelRequest, onClose]);

  const normalizedDetails = resolvedDetails;
  const mergedHiddenKeys =
    variant === "transfer-abroad"
      ? [...hiddenKeys, ...transferAbroadHiddenKeys]
      : hiddenKeys;
  const title =
    normalizedDetails?.TransactionType ||
    normalizedDetails?.Type ||
    "Transaction Details";
  const status = normalizedDetails?.Status || normalizedDetails?.TransferStatus;
  const detailsView = buildTransactionDetailsView(normalizedDetails, {
    variant,
    feature,
    hiddenKeys: mergedHiddenKeys,
  });
  const detailSections = detailsView.sections;
  const transferAbroadView = detailsView.transferAbroad;
  const autoActions = useMemo(() => {
    if (!normalizedDetails) return [];

    const actions = [];
    const codeValue = normalizedDetails?.QRCode || normalizedDetails?.Code || "";
    const transactionType = String(normalizedDetails?.TransactionType || "").toLowerCase();
    const isCashWithdrawal = transactionType.includes("cash withdrawal");

    if (normalizedDetails?.ShowCopyCode && normalizedDetails?.Code) {
      actions.push({
        key: "copy-code",
        label: isCopied("transaction-details-copy") ? "Copied!" : "Copy Code",
        className: isCopied("transaction-details-copy") ? "is-copied" : "",
        icon: faCopy,
        onClick: async () => {
          await copyWithFeedback("transaction-details-copy", normalizedDetails.Code);
        },
      });
    }

    if (normalizedDetails?.ShowDownloadQR && codeValue) {
      actions.push({
        key: "download-qr",
        label: "Download QR",
        icon: faDownload,
        onClick: () => setQrCode(String(codeValue)),
      });
    }

    if (normalizedDetails?.ShowCancelButler) {
      actions.push({
        key: "cancel-butler",
        label: "Cancel Butler",
        icon: faBan,
        onClick: () =>
          setCancelRequest({
            type: "butler",
            transactionId: normalizedDetails.TransactionID || normalizedDetails.OrderID,
            title: "Cancel Butler Request",
            description:
              "Your butler request has not been processed yet. If you cancel, you will need to start a new request.",
            confirmLabel: "Cancel Butler",
          }),
      });
    }

    if (normalizedDetails?.ShowCancelCode && normalizedDetails?.TransactionID) {
      actions.push({
        key: "cancel-request",
        label: isCashWithdrawal ? "Cancel Withdrawal" : "Cancel Deposit",
        icon: faBan,
        onClick: () =>
          setCancelRequest({
            type: "cash",
            flowType: isCashWithdrawal ? "cashout" : "cashin",
            transactionId: normalizedDetails.TransactionID,
            title: isCashWithdrawal
              ? "Are you sure you want to cancel the cash withdrawal?"
              : "Are you sure you want to cancel the cash deposit?",
            description: isCashWithdrawal
              ? "Your withdrawal request has not been processed yet. If you cancel, no funds will be moved."
              : "Your deposit request has not been processed yet. If you cancel, you will need to start a new request.",
            confirmLabel: isCashWithdrawal ? "Cancel Withdrawal" : "Cancel Deposit",
          }),
      });
    }

    return actions;
  }, [copyWithFeedback, isCopied, normalizedDetails]);
  const baseExtraActions = Array.isArray(extraActions)
    ? extraActions.filter((action) => action?.visible !== false)
    : [];
  const visibleExtraActions = useMemo(() => {
    const merged = [...autoActions];
    const existingKeys = new Set(merged.map((action) => action?.key));

    baseExtraActions.forEach((action) => {
      if (!action?.key || !existingKeys.has(action.key)) {
        merged.push(action);
        if (action?.key) existingKeys.add(action.key);
      }
    });

    return merged;
  }, [autoActions, baseExtraActions]);
  const actionsCount =
    (normalizedDetails?.ShowDownloadReceipt ? 1 : 0) +
    visibleExtraActions.length;
  const isSingleAction = actionsCount === 1;
  const transferAbroadRepeatAction = visibleExtraActions.find(
    (action) => action?.key === "repeat",
  );
  const isPayrollDetailsType =
    feature === "payroll" ||
    String(normalizedDetails?.TransactionType || "").toLowerCase().includes("payroll");
  const isQuickPayDetailsType = String(
    normalizedDetails?.TransactionType || "",
  )
    .toLowerCase()
    .includes("quick pay");
  const canViewOverviewDetails = Boolean(
    normalizedDetails?.ShowPayrollDetailsButton ||
      normalizedDetails?.ShowOverviewButton,
  );
  const canApproveRejectDetails =
    Boolean(normalizedDetails?.ShowApproveButton) &&
    String(normalizedDetails?.Status || "").toLowerCase().includes("pending") &&
    !isViewer;
  const showApprovalActions = canViewOverviewDetails || canApproveRejectDetails;
  const summarySecondaryText = normalizedDetails?.Amount2
    ? formatTransactionAmount(
        normalizedDetails.Amount2,
        normalizedDetails.CurrencyID2,
      )
    : detailsView.kind === "cash"
      ? null
      : normalizedDetails?.SubTitle || null;
  const isOpen = Boolean(
    transactionId || normalizedDetails || resolvedLoading || resolvedError,
  );
  const panelClassName = `new-transaction-details${
    variant === "transfer-abroad"
      ? " new-transaction-details--transfer-abroad"
      : ""
  }`;

  const panelContent = (
    <>
      <div className="new-transaction-details__header">
        <div>
          <span>Transaction Details</span>
          <h2>{title}</h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close details panel">
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      {resolvedLoading ? (
        <TransactionDetailsSkeleton />
      ) : resolvedError ? (
        <div className="new-transaction-details__empty">
          <FontAwesomeIcon icon={faCircleInfo} />
          <p>Unable to load transaction details right now.</p>
        </div>
      ) : normalizedDetails ? (
        <>
          {variant === "transfer-abroad" ? (
            <>
              <div className="new-transaction-details__summary">
                <span className="new-transaction-details__icon" aria-hidden="true">
                  <SummaryIcon iconUrl={normalizedDetails?.IconUrl} />
                </span>
                <strong>
                  {formatTransferAbroadAmount(normalizedDetails?.Amount, normalizedDetails)}
                </strong>
                {normalizedDetails?.AmountReceived ? (
                  <small>
                    Recipient receives{" "}
                    {formatTransferAbroadAmount(
                      normalizedDetails.AmountReceived,
                      normalizedDetails,
                    )}
                  </small>
                ) : null}
                {status ? <StatusBadge value={status} /> : null}
              </div>

              {transferAbroadView?.overviewRows?.length ? (
                <div className="new-transaction-details__section new-transaction-details__section--top">
                  <h3>Overview</h3>
                  <dl>
                    {transferAbroadView.overviewRows.map((item) => (
                      <div className="row g-2" key={item.key}>
                        <dt className="col-12 col-sm-6">{item.label}</dt>
                        <dd className="col-12 col-sm-6">
                          <RenderFieldValue item={item} />
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ) : null}

              {transferAbroadView?.detailsRows?.length ? (
                <div className="new-transaction-details__section container-fluid">
                  <h3>Details</h3>
                  <dl>
                    {transferAbroadView.detailsRows.map((item) => (
                      <div className="row g-2" key={item.key}>
                        <dt className="col-12 col-sm-6">{item.label}</dt>
                        <dd className="col-12 col-sm-6">
                          <RenderFieldValue item={item} />
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ) : null}

              {transferAbroadView?.amountRows?.length ? (
                <div className="new-transaction-details__section container-fluid">
                  <h3>Amounts</h3>
                  <dl>
                    {transferAbroadView.amountRows.map((item) => (
                      <div className="row g-2" key={item.key}>
                        <dt className="col-12 col-sm-6">{item.label}</dt>
                        <dd className="col-12 col-sm-6">
                          <RenderFieldValue item={item} />
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ) : null}

              {(normalizedDetails?.ShowDownloadReceipt ||
                Boolean(transferAbroadRepeatAction)) && (
                <div className="new-transaction-details__actions row g-2">
                  {normalizedDetails?.ShowDownloadReceipt ? (
                    <div className="col-12">
                      <HTMLtoPDFGenerator data={normalizedDetails?.Receipt || null} />
                    </div>
                  ) : null}

                  {transferAbroadRepeatAction ? (
                    <div className="col-12">
                      <button
                        type="button"
                        className="new-transaction-details__flag-button"
                        disabled={transferAbroadRepeatAction.disabled}
                        onClick={transferAbroadRepeatAction.onClick}
                      >
                        {transferAbroadRepeatAction.icon ? (
                          <FontAwesomeIcon icon={transferAbroadRepeatAction.icon} />
                        ) : null}
                        <span>{transferAbroadRepeatAction.label}</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="new-transaction-details__summary">
                <span className="new-transaction-details__icon" aria-hidden="true">
                  <SummaryIcon iconUrl={normalizedDetails?.IconUrl} />
                </span>
                <strong>
                  {formatTransactionAmount(
                    normalizedDetails?.Amount,
                    normalizedDetails?.CurrencyID,
                  )}
                </strong>
                {summarySecondaryText ? <small>{summarySecondaryText}</small> : null}
                {status ? <StatusBadge value={status} /> : null}
              </div>

              {detailSections.map((section) => (
                <div
                  className="new-transaction-details__section container-fluid"
                  key={section.key}
                >
                  <h3>{section.title}</h3>
                  <dl>
                    {section.rows.map((item) => (
                      <div className="row g-2" key={item.key}>
                        <dt className="col-12 col-sm-6">{item.label}</dt>
                        <dd className="col-12 col-sm-6">
                          <RenderFieldValue item={item} />
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}

              {(normalizedDetails?.ShowDownloadReceipt ||
                visibleExtraActions.length > 0) && (
                <div className="new-transaction-details__actions row g-2">
                  {normalizedDetails?.ShowDownloadReceipt ? (
                    <div className="col-12">
                      <HTMLtoPDFGenerator data={normalizedDetails?.Receipt || null} />
                    </div>
                  ) : null}

                  {visibleExtraActions.map((action) => (
                    <div className={isSingleAction ? "col-12" : "col-12 col-sm-6"} key={action.key}>
                      <button
                        type="button"
                        className={`new-transaction-details__flag-button ${
                          action.className || ""
                        }`.trim()}
                        disabled={action.disabled}
                        onClick={action.onClick}
                      >
                        {action.icon ? <FontAwesomeIcon icon={action.icon} /> : null}
                        <span>{action.label}</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {showApprovalActions ? (
                <div className="new-transaction-details__actions new-transaction-details__actions--payroll">
                  {canViewOverviewDetails ? (
                    <>
                      <button
                        type="button"
                        className="new-transaction-details__flag-button"
                        onClick={handleViewOverview}
                      >
                        <FontAwesomeIcon icon={faFileLines} />
                        <span>{isQuickPayDetailsType ? "View Quick Pay" : "View Payroll"}</span>
                      </button>
                      {overviewActionError ? (
                        <p className="new-transaction-details__action-error">
                          {overviewActionError}
                        </p>
                      ) : null}
                    </>
                  ) : null}

                  {canApproveRejectDetails ? (
                    <div className="new-transaction-details__approve-reject-row">
                      <button
                        type="button"
                        className="new-transaction-details__flag-button is-approve"
                        onClick={() => handleApproveReject("Approve")}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                        <span>Approve</span>
                      </button>
                      <button
                        type="button"
                        className="new-transaction-details__flag-button is-reject"
                        onClick={() => handleApproveReject("Reject")}
                      >
                        <FontAwesomeIcon icon={faXmarkCircle} />
                        <span>Reject</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </>
      ) : (
        <div className="new-transaction-details__empty">
          <FontAwesomeIcon icon={faCircleInfo} />
          <p>Select a transaction to view its details.</p>
        </div>
      )}
    </>
  );

  return (
    <>
      {isMobileViewport ? (
        <Modal
          centered
          show={isOpen}
          onHide={onClose}
          dialogClassName="new-transaction-details-modal"
          contentClassName="new-transaction-details-modal__content"
        >
          <Modal.Body className="new-transaction-details-modal__body">
            <div
              className={`${panelClassName} new-transaction-details--popup`}
              aria-label="Transaction Details"
            >
              {panelContent}
            </div>
          </Modal.Body>
        </Modal>
      ) : (
        <aside className={panelClassName} aria-label="Transaction Details">
          {panelContent}
        </aside>
      )}
      <CancelActionModal
        cancelRequest={cancelRequest}
        onClose={() => setCancelRequest(null)}
        onConfirm={handleConfirmCancel}
      />
      <QrDownload qrCodeData={qrCode} setQrData={setQrCode} />
    </>
  );
}
